/**
 * PaymentErrorState — terminal error states on public payment pages.
 *
 * reason variants:
 *   'not_found' | 'expired'  → link invalid, ask for a new one
 *   'used'                   → already paid via this link
 *   'invoice_not_payable'    → invoice already paid another way
 *   'rate_limited'           → too many checkout attempts
 *   'service_unavailable'    → Stripe 502
 *   'generic'                → catch-all
 */

import { AlertCircle, CheckCircle, Clock } from 'lucide-react';

export type PaymentErrorReason =
  | 'not_found'
  | 'expired'
  | 'used'
  | 'invoice_not_payable'
  | 'rate_limited'
  | 'service_unavailable'
  | 'generic';

const BILLING_EMAIL = 'billing@lyokotech.com';

function config(reason: PaymentErrorReason) {
  switch (reason) {
    case 'not_found':
    case 'expired':
      return {
        Icon: Clock,
        cls: 'text-yellow-400',
        title: 'Link not found or expired',
        msg: 'This payment link is no longer valid. Please contact us for a new one.',
        contact: true,
      };
    case 'used':
      return {
        Icon: AlertCircle,
        cls: 'text-yellow-400',
        title: 'Link already used',
        msg: 'This payment link has already been used. If you believe this is an error, contact us.',
        contact: true,
      };
    case 'invoice_not_payable':
      return {
        Icon: CheckCircle,
        cls: 'text-green-400',
        title: 'Invoice already paid',
        msg: 'This invoice has already been paid. Thank you!',
        contact: false,
      };
    case 'rate_limited':
      return {
        Icon: Clock,
        cls: 'text-yellow-400',
        title: 'Too many attempts',
        msg: 'Too many checkout attempts. Please wait a minute and try again.',
        contact: false,
      };
    case 'service_unavailable':
      return {
        Icon: AlertCircle,
        cls: 'text-red-400',
        title: 'Service unavailable',
        msg: 'Payment service is temporarily unavailable. Please try again.',
        contact: false,
      };
    default:
      return {
        Icon: AlertCircle,
        cls: 'text-red-400',
        title: 'Something went wrong',
        msg: 'Something went wrong. Please try again or contact us.',
        contact: true,
      };
  }
}

export function PaymentErrorState({ reason }: { reason: PaymentErrorReason }) {
  const { Icon, cls, title, msg, contact } = config(reason);
  return (
    <div className="text-center py-8" role="alert">
      <Icon className={`h-12 w-12 mx-auto mb-4 ${cls}`} aria-hidden="true" />
      <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">{title}</h2>
      <p className="text-gray-600 dark:text-gray-300 max-w-sm mx-auto">{msg}</p>
      {contact && (
        <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
          <a
            href={`mailto:${BILLING_EMAIL}`}
            className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 underline"
          >
            {BILLING_EMAIL}
          </a>
        </p>
      )}
    </div>
  );
}
